import React, { useEffect, useMemo, useState } from 'react';
import { PaperclipIcon, ShieldCheckIcon } from 'lucide-react';
import type { AdminRole } from '../../../types/config';
import { useStore } from '../../../contexts/StoreContext';
import { useCaseActions } from '../../../hooks/useCaseActions';
import { ADMIN_USERS } from '../../../data/users';
import { Modal } from '../../shared/Modal';
import { PAYMENT_LABEL, isPaid } from '../../../utils/caseStatus';
import { taka } from '../../../utils/bn';
const METHODS = ['সরকারি ট্রেজারি চালান', 'ব্যাংক চালান (সোনালী ব্যাংক)', 'নগদ — অফিস ক্যাশ কাউন্টার', 'পে-অর্ডার'];
interface Props {
  open: boolean;
  onClose: () => void;
  role: AdminRole;
  initialPcr?: string;
  onDone?: (pcr: string) => void;
}

/** Authorized officer records a payment received at the office; it is stored as reconciled with its challan reference. */
export function OfficePaymentModal({
  open,
  onClose,
  role,
  initialPcr,
  onDone
}: Props) {
  const {
    cases
  } = useStore();
  const actions = useCaseActions(role);
  const eligible = useMemo(() => cases.filter(c => c.stage !== 'released' && !isPaid(c)), [cases]);
  const [pcr, setPcr] = useState('');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState(METHODS[0]);
  const [challan, setChallan] = useState('');
  const [date, setDate] = useState('2026-10-15');
  const [officer, setOfficer] = useState(ADMIN_USERS[role].name);
  const [file, setFile] = useState('');
  const [checked, setChecked] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [error, setError] = useState('');
  const c = cases.find(x => x.pcr === pcr);
  const due = c ? c.fine + c.extraCharge : 0;
  useEffect(() => {
    if (!open) return;
    const start = initialPcr && eligible.some(x => x.pcr === initialPcr) ? initialPcr : eligible[0]?.pcr ?? '';
    setPcr(start);
    setMethod(METHODS[0]);
    setChallan('');
    setFile('');
    setChecked(false);
    setRemarks('');
    setError('');
    setOfficer(ADMIN_USERS[role].name);
  }, [open, initialPcr, eligible, role]);
  useEffect(() => {
    if (c) setAmount(String(c.fine + c.extraCharge));
  }, [pcr]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = () => {
    if (!c) return setError('একটি PCR নির্বাচন করুন।');
    if (Number(amount) !== due) return setError(`আদায়কৃত অর্থ মোট প্রদেয় ${taka(due)}-এর সমান হতে হবে।`);
    if (challan.trim().length < 6) return setError('সঠিক সরকারি রসিদ / চালান নম্বর লিখুন।');
    if (!file) return setError('চালান বা রসিদের স্ক্যান কপি সংযুক্ত করুন।');
    if (!checked) return setError('মূল চালান যাচাইয়ের ঘোষণা নিশ্চিত করুন।');
    actions.recordOfficePayment(c.pcr, {
      method,
      challan: challan.trim(),
      date: new Date(`${date}T12:00:00`).toISOString(),
      officer,
      amount: due,
      supportingDoc: file,
      remarks
    });
    onDone?.(c.pcr);
    onClose();
  };
  return <Modal open={open} onClose={onClose} title="অফিসে জরিমানা আদায় লিপিবদ্ধকরণ" size="lg" footer={<><button className="btn btn-outline" onClick={onClose} data-id="element-279" data-mp="lryg6smueu0w">বাতিল</button><button className="btn btn-green" onClick={submit} data-id="element-280" data-mp="vxzsxia4065q"><ShieldCheckIcon className="h-4 w-4" data-id="element-281" data-mp="vzuf0zx4a4be" /> লিপিবদ্ধ ও সমন্বয় করুন</button></>} data-id="element-278" data-mp="gu6nhwedj3vn">
      <p className="mb-4 rounded-lg bg-navy-50 px-3 py-2 text-xs text-navy" data-id="element-282" data-mp="qzezq2t9ymqs">শুধুমাত্র অনুমোদিত কর্মকর্তা মূল সরকারি চালান / রসিদ যাচাই করে এই রেকর্ড করতে পারবেন। এটি “অফিসে লিপিবদ্ধ” হিসেবে অনলাইন পেমেন্ট থেকে আলাদা দেখানো হবে।</p>
      <div className="grid gap-4 md:grid-cols-2" data-id="element-283" data-mp="d78lwndlgrui">
        <div data-id="element-284" data-mp="d81iiviua0yx">
          <label className="label" htmlFor="op-pcr" data-id="element-285" data-mp="d8jnqhnhbpiz">PCR নম্বর</label>
          <select id="op-pcr" className="input" value={pcr} onChange={e => setPcr(e.target.value)} data-id="element-286" data-mp="8741wndh9ezj">
            {eligible.map(x => <option key={x.pcr} value={x.pcr} data-id="element-287" data-mp="39km3j14su5h">{x.pcr} — {x.regNo} ({PAYMENT_LABEL[x.paymentStatus].label})</option>)}
          </select>
          {c && <p className="mt-1 text-xs text-ink-muted" data-id="element-288" data-mp="1xmtq4vawrngc">জরিমানা {taka(c.fine)} + অতিরিক্ত চার্জ {taka(c.extraCharge)} = <b className="text-ink" data-id="element-289" data-mp="1tlrqidd2x9du">{taka(due)}</b> (নমুনা পরিমাণ)</p>}
        </div>
        <div data-id="element-290" data-mp="20oosa5532ut6">
          <label className="label" htmlFor="op-amt" data-id="element-291" data-mp="20oy9p4c2btn9">আদায়কৃত অর্থ (৳)</label>
          <input id="op-amt" className="input" inputMode="numeric" value={amount} onChange={e => setAmount(e.target.value.replace(/[^0-9]/g, ''))} data-id="element-292" data-mp="20mvl8oqk68kn" />
        </div>
        <div data-id="element-293" data-mp="1rn8jg1tx30r2">
          <label className="label" htmlFor="op-m" data-id="element-294" data-mp="1rnqn3jiwsh90">আদায়ের পদ্ধতি</label>
          <select id="op-m" className="input" value={method} onChange={e => setMethod(e.target.value)} data-id="element-295" data-mp="1vok6q0pbj9w3">{METHODS.map(m => <option key={m} data-id="element-296" data-mp="1hwm8ikx5ayep">{m}</option>)}</select>
        </div>
        <div data-id="element-297" data-mp="1mpfefc4cocsd">
          <label className="label" htmlFor="op-ch" data-id="element-298" data-mp="1mpyuenafwr3r">সরকারি রসিদ / চালান নম্বর</label>
          <input id="op-ch" className="input font-mono" placeholder="CHL-TEJ-2026-XXXX" value={challan} onChange={e => setChallan(e.target.value.toUpperCase())} data-id="element-299" data-mp="180j4tamnarpn" />
        </div>
        <div data-id="element-300" data-mp="yzckeqgvkwr4">
          <label className="label" htmlFor="op-d" data-id="element-301" data-mp="yyt4ffascifq">রসিদের তারিখ</label>
          <input id="op-d" type="date" className="input" value={date} onChange={e => setDate(e.target.value)} data-id="element-302" data-mp="z1p2d3mmv746" />
        </div>
        <div data-id="element-303" data-mp="kc45pfocy9ae">
          <label className="label" htmlFor="op-o" data-id="element-304" data-mp="kbuh9wiyoa6t">গ্রহণকারী কর্মকর্তা</label>
          <input id="op-o" className="input" value={officer} onChange={e => setOfficer(e.target.value)} data-id="element-305" data-mp="fdsxf9dpo6ua" />
        </div>
        <div className="md:col-span-2" data-id="element-306" data-mp="tdkfpmb9g9e3">
          <p className="label" data-id="element-307" data-mp="ten4nop0w1rd">সহায়ক কাগজ (চালানের স্ক্যান কপি)</p>
          <div className="flex flex-wrap items-center gap-2" data-id="element-308" data-mp="panwpuht6b9m">
            <label className="btn btn-outline btn-sm" data-id="element-309" data-mp="13b8cehsffqyw">
              ফাইল নির্বাচন
              <input type="file" accept="image/*,application/pdf" className="sr-only" onChange={e => e.target.files?.[0] && setFile(e.target.files[0].name)} data-id="element-310" data-mp="1395e7ah4erj3" />
            </label>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setFile(`challan_${pcr.slice(-3)}_scan.pdf`)} data-id="element-311" data-mp="1d0i8e8qe5tsa">ডেমো স্ক্যান সংযুক্ত করুন</button>
            {file && <span className="flex items-center gap-1 rounded bg-success-50 px-2 py-1 text-xs text-success" data-id="element-312" data-mp="1m2rf0nijh30e"><PaperclipIcon className="h-3 w-3" data-id="element-313" data-mp="1h3xysnbvaxwj" />{file}</span>}
          </div>
        </div>
        <div className="md:col-span-2" data-id="element-314" data-mp="eq9xqgyissgi">
          <label className="label" htmlFor="op-r" data-id="element-315" data-mp="erbgl7ls2j8s">মন্তব্য (ঐচ্ছিক)</label>
          <input id="op-r" className="input" value={remarks} onChange={e => setRemarks(e.target.value)} data-id="element-316" data-mp="jpd3y4q8l4q2" />
        </div>
      </div>
      <label className="mt-4 flex items-start gap-2 text-sm text-ink" data-id="element-317" data-mp="no2dfm85m24f">
        <input type="checkbox" className="mt-1 h-4 w-4 accent-bdgreen" checked={checked} onChange={e => setChecked(e.target.checked)} data-id="element-318" data-mp="sigbxxi44pdb" />
        আমি মূল সরকারি চালান / রসিদ যাচাই করেছি এবং অর্থ সরকারি কোষাগারে জমার বিষয়টি নিশ্চিত হয়েছি।
      </label>
      {error && <p className="mt-3 rounded-lg bg-danger-50 px-3 py-2 text-sm text-danger-700" role="alert" data-id="element-319" data-mp="17dmkos8lf2ac">{error}</p>}
    </Modal>;
}